// LivestreamViewerCamera.jsx
import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { createPortal } from 'react-dom';

import {
  getLocalVideoTrack,
  getIsLocalVideoMirrored,
  getLocalAudioTrack,
  subscribeLocalTracksReleased,
} from '../resource/agoraLocalTracks.js';
import { LivestreamPlayerContext } from '../context/LivestreamPlayerContext.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import { observeVideoIntrinsicSize } from '../resource/videoIntrinsicSize.js';
import { VIEWER_CAMERA_PREPARING_LEAD_SECONDS } from '../resource/liveStreamConstants.js';
import {
  EXCLUSIVE_VIEWER_FLOAT_Z_INDEX,
  EXCLUSIVE_VIEWER_RIGHT,
  exclusiveViewerWindowSizeStyle,
  exclusiveViewerWindowVisualStyle,
  getExclusiveViewerFloatingConfig,
  getPipWindowStyle,
  getExclusiveViewerWindowBottom,
} from '../resource/exclusiveViewerStyle.js';
import { ModalId } from '../resource/trackingConstants.js';
import { color } from '../style/variables.js';

import useDraggableFloatingWindow from '../hook/useDraggableFloatingWindow.js';
import useTimeWatcher from '../hook/useTimeWatcher.js';

import MicVolumeMeter from '../component/MicVolumeMeter.jsx';
import MicVideoButtons from '../component/MicVideoButtons.jsx';

import MoreButtonIconSrc from '../../img/ic-topbar-more.svg';

// main player absolute position
const DEFAULT_MAIN_WINDOW_STYLE = { top: 0, left: 0, right: 0, bottom: 0 };

const EXCLUSIVE_VIEWER_CAMERA_TRACKING_PAYLOAD = {
  modalId: ModalId.LiveStream.ModalExclusiveCamera,
};

// Countdown seconds UI during VIEWER_CAMERA_PREPARING_LEAD_SECONDS in exclusive preparing stage
const ViewerCameraCountdown = ({ performingStartUnix = 0 }) => {
  const seconds = useTimeWatcher(({ nowUnixTimestamp }) => {
    const remaining = Math.ceil(performingStartUnix - nowUnixTimestamp);
    return remaining > 0 && remaining <= VIEWER_CAMERA_PREPARING_LEAD_SECONDS
      ? remaining
      : 0;
  });

  if (seconds <= 0) {
    return null;
  }
  return <CountdownOverlay>{seconds}</CountdownOverlay>;
};

ViewerCameraCountdown.propTypes = {
  performingStartUnix: PropTypes.number,
};

const LivestreamViewerCamera = ({
  channelId = '',
  isPreviewReady = false,
  isPublishing = false,
  isAudioEnabled = true,
  isVideoEnabled = true,
  cameraSwitchVersion = 0,
  isInMainWindow = false,
  isPerforming = false,
  isPreparing = false,
  hasMediaAccessError = false,
  performingStartUnix = 0,
  hasTopGift = false,
  startPreviewing = NULL_FUNCTION,
  invalidatePreview = NULL_FUNCTION,
  startPublishing = NULL_FUNCTION,
  retryPreviewing = NULL_FUNCTION,
  stopPublishing = NULL_FUNCTION,
  setAudioEnabled = NULL_FUNCTION,
  setVideoEnabled = NULL_FUNCTION,
  openMoreMenu = NULL_FUNCTION,
}) => {
  const videoContainerRef = useRef(null);

  // Enable drag only when viewer are in mini window (camera on, not muted)
  const isMiniWindowDraggable =
    isPreviewReady && isVideoEnabled && !isInMainWindow;
  const floatingConfig = useMemo(
    () => getExclusiveViewerFloatingConfig({ hasTopGift }),
    [hasTopGift]
  );
  const miniWindowBottom = getExclusiveViewerWindowBottom({ hasTopGift });
  const floatingWindow = useDraggableFloatingWindow({
    isEnabled: isMiniWindowDraggable,
    ...floatingConfig,
  });

  // While the viewer is in the main window the controls belong to the streamer
  // PiP, so they must mount inside its shell to share its drag transform.
  const { pipControlsMount } = useContext(LivestreamPlayerContext) ?? {};

  // Agora join failures can release tracks while this component stays mounted.
  useEffect(() => {
    if (!channelId) return;
    return subscribeLocalTracksReleased({
      listener: ({ channelId: releasedChannelId }) => {
        if (releasedChannelId === channelId) {
          invalidatePreview();
        }
      },
    });
  }, [channelId, invalidatePreview]);

  // Prepare tracks first. This also handles a direct PERFORMING mount without
  // coupling device access to Agora publishing.
  useEffect(() => {
    if (!isPreviewReady && channelId && (isPreparing || isPerforming)) {
      startPreviewing();
    }
  }, [startPreviewing, channelId, isPreviewReady, isPreparing, isPerforming]);

  // Publish only after the prepared tracks have made the self-view ready.
  useEffect(() => {
    if (isPreviewReady && !isPublishing && channelId && isPerforming) {
      startPublishing();
    }
  }, [startPublishing, channelId, isPreviewReady, isPublishing, isPerforming]);

  // Stop publishing on unmount (release tracks, turn off camera light).
  useEffect(() => () => stopPublishing(), [stopPublishing]);

  // Once the camera is on, render the local camera into the container.
  // Mirroring is applied with CSS on the container (see isVideoMirrored)
  // instead of Agora's play() config: re-playing with a new mirror value does
  // not reliably re-apply on every device, and CSS updates without stopping
  // the track. Agora mirrors local tracks by default, so it must be off here.
  useEffect(() => {
    if (!isPreviewReady || !isVideoEnabled || !channelId) return;
    const videoTrack = getLocalVideoTrack({ channelId });
    if (!videoTrack || !videoContainerRef.current) return;
    if (videoTrack.isPlaying) {
      videoTrack.stop();
    }
    videoTrack.play(videoContainerRef.current, {
      fit: 'cover',
      mirror: false,
    });
  }, [channelId, isPreviewReady, isVideoEnabled]);

  // Agora keeps the same element across a camera switch, so cameraSwitchVersion
  // only re-reads; the video events carry the actual change.
  const [videoSize, setVideoSize] = useState(null);
  useEffect(
    () =>
      observeVideoIntrinsicSize({
        video: videoContainerRef.current?.querySelector('video'),
        onSize: setVideoSize,
      }),
    [channelId, isPreviewReady, isVideoEnabled, cameraSwitchVersion]
  );

  // A new session must not keep the previous camera's frame.
  useEffect(() => setVideoSize(null), [channelId]);

  const miniWindowStyle = useMemo(
    () => ({ ...floatingWindow.style, ...getPipWindowStyle({ ...videoSize }) }),
    [floatingWindow.style, videoSize]
  );

  // Volume meter samples the current audio track level
  const getAudioLevel = useCallback(
    () => getLocalAudioTrack({ channelId })?.getVolumeLevel?.() ?? 0,
    [channelId]
  );

  const handleToggleAudio = useCallback(
    () => setAudioEnabled(!isAudioEnabled),
    [setAudioEnabled, isAudioEnabled]
  );
  const handleToggleVideo = useCallback(
    () => setVideoEnabled(!isVideoEnabled),
    [setVideoEnabled, isVideoEnabled]
  );

  // cameraSwitchVersion / isPreviewReady changes are the only events that
  // move the facing; re-read the registry on those edges so the CSS mirror
  // follows the front camera without replaying the track.
  const [isVideoMirrored, setIsVideoMirrored] = useState(true);
  useEffect(() => {
    setIsVideoMirrored(getIsLocalVideoMirrored({ channelId }));
  }, [channelId, cameraSwitchVersion, isPreviewReady]);

  if (!isPreviewReady) {
    if (!hasMediaAccessError) {
      return null;
    }
    // Capture failed (permission / device): keep the off panel up; tapping
    // either button retries capture directly (a denied retry opens the
    // permission asker).
    return (
      <ViewerCameraOffPanel
        className="swiper-no-swiping"
        $bottom={miniWindowBottom}
      >
        <MicVideoButtons
          isAudioOn={false}
          isVideoOn={false}
          hasBorder
          trackingPayload={EXCLUSIVE_VIEWER_CAMERA_TRACKING_PAYLOAD}
          onToggleAudio={retryPreviewing}
          onToggleVideo={retryPreviewing}
        />
      </ViewerCameraOffPanel>
    );
  }

  // The off panel sits on a solid background, so its buttons need a border.
  const renderMicVideoButtons = ({ hasBorder }) => (
    <MicVideoButtons
      isAudioOn={isAudioEnabled}
      isVideoOn={isVideoEnabled}
      hasBorder={hasBorder}
      trackingPayload={EXCLUSIVE_VIEWER_CAMERA_TRACKING_PAYLOAD}
      onToggleAudio={handleToggleAudio}
      onToggleVideo={handleToggleVideo}
    />
  );

  const floatingControls = (
    <>
      <VolumeMeterContainer>
        <MicVolumeMeter getAudioLevel={getAudioLevel} />
      </VolumeMeterContainer>
      <MoreButton data-no-drag onClick={openMoreMenu}>
        <MoreIcon src={MoreButtonIconSrc} />
      </MoreButton>
      <Controls>{renderMicVideoButtons({ hasBorder: false })}</Controls>
    </>
  );

  return (
    <>
      <ViewerCameraContainer
        ref={floatingWindow.ref}
        $isMain={isInMainWindow}
        className={isInMainWindow ? undefined : 'swiper-no-swiping'}
        style={isInMainWindow ? DEFAULT_MAIN_WINDOW_STYLE : miniWindowStyle}
      >
        <ViewerCameraWindow
          $isMain={isInMainWindow}
          $isHidden={!isVideoEnabled}
        >
          <VideoCameraWindow
            ref={videoContainerRef}
            $isMirrored={isVideoMirrored}
          />
        </ViewerCameraWindow>
        {/* PREPARING countdown before PERFORMING */}
        {isPreparing && performingStartUnix > 0 && isVideoEnabled && (
          <ViewerCameraCountdown performingStartUnix={performingStartUnix} />
        )}
        {isVideoEnabled && !isInMainWindow && floatingControls}
      </ViewerCameraContainer>

      {!isVideoEnabled && (
        <ViewerCameraOffPanel
          className="swiper-no-swiping"
          $bottom={miniWindowBottom}
        >
          {renderMicVideoButtons({ hasBorder: true })}
        </ViewerCameraOffPanel>
      )}

      {isVideoEnabled &&
        isInMainWindow &&
        (pipControlsMount ? (
          createPortal(floatingControls, pipControlsMount)
        ) : (
          <MiniWindowControls $bottom={miniWindowBottom}>
            {floatingControls}
          </MiniWindowControls>
        ))}
    </>
  );
};

LivestreamViewerCamera.propTypes = {
  channelId: PropTypes.string,
  isPreviewReady: PropTypes.bool,
  isPublishing: PropTypes.bool,
  isAudioEnabled: PropTypes.bool,
  isVideoEnabled: PropTypes.bool,
  cameraSwitchVersion: PropTypes.number,
  isInMainWindow: PropTypes.bool,
  isPerforming: PropTypes.bool,
  isPreparing: PropTypes.bool,
  hasMediaAccessError: PropTypes.bool,
  performingStartUnix: PropTypes.number,
  hasTopGift: PropTypes.bool,
  startPreviewing: PropTypes.func,
  invalidatePreview: PropTypes.func,
  startPublishing: PropTypes.func,
  retryPreviewing: PropTypes.func,
  stopPublishing: PropTypes.func,
  setAudioEnabled: PropTypes.func,
  setVideoEnabled: PropTypes.func,
  openMoreMenu: PropTypes.func,
};

const ViewerCameraContainer = styled.div`
  position: absolute;
  z-index: ${({ $isMain }) => ($isMain ? -1 : EXCLUSIVE_VIEWER_FLOAT_Z_INDEX)};
  ${({ $isMain }) => !$isMain && exclusiveViewerWindowSizeStyle}
`;

const ViewerCameraWindow = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: ${color.neutral[5]};
  display: ${({ $isHidden }) => ($isHidden ? 'none' : 'block')};
  ${({ $isMain }) => !$isMain && exclusiveViewerWindowVisualStyle}
`;

const VideoCameraWindow = styled.div`
  width: 100%;
  height: 100%;
  background: ${color.neutral[0]};
  border-radius: 4px;
  transform: ${({ $isMirrored }) => ($isMirrored ? 'scaleX(-1)' : 'none')};
`;

const CountdownOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: ${EXCLUSIVE_VIEWER_FLOAT_Z_INDEX + 1};
  font-size: 40px;
  color: ${color.white};
  pointer-events: none;
`;

const ViewerCameraOffPanel = styled.div`
  position: absolute;
  right: ${EXCLUSIVE_VIEWER_RIGHT}px;
  bottom: ${({ $bottom }) => $bottom}px;
  z-index: ${EXCLUSIVE_VIEWER_FLOAT_Z_INDEX};
  padding: 8px;
  border-radius: 12px;
  background: ${color.neutral[10]};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const Controls = styled.div`
  position: absolute;
  left: 50%;
  bottom: 4px;
  transform: translateX(-50%);
  z-index: ${EXCLUSIVE_VIEWER_FLOAT_Z_INDEX};
  display: flex;
  gap: 8px;
  pointer-events: auto;
`;

const MiniWindowControls = styled.div`
  position: absolute;
  right: ${EXCLUSIVE_VIEWER_RIGHT}px;
  bottom: ${({ $bottom }) => $bottom}px;
  ${exclusiveViewerWindowSizeStyle}
  z-index: ${EXCLUSIVE_VIEWER_FLOAT_Z_INDEX + 1};
  pointer-events: none;
`;

const MoreButton = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: ${EXCLUSIVE_VIEWER_FLOAT_Z_INDEX};
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MoreIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
  pointer-events: none;
  transform: rotate(90deg);
`;

const VolumeMeterContainer = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: ${EXCLUSIVE_VIEWER_FLOAT_Z_INDEX};
  pointer-events: none;
`;

export default LivestreamViewerCamera;
